var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines */
import { useState } from 'react';
export const useCFOptions = ({ allowMultiple = false, initialValue = '', initialOptions = [], onChange, }) => {
    //
    // Internal utility functions
    //
    // Ensures that the value provided `v` conforms to the expected type
    // before updating official `value`.
    const bufferValueType = (v) => {
        // First if no value is provided we ensure an appropriate one is set;
        // either an empty array or an empty string.
        if (!v)
            return allowMultiple ? [] : '';
        // If the value is a string we can simply format it
        // depending on whether multiple values are expected
        if (typeof v === 'string')
            return allowMultiple ? [v] : v;
        // From here forward we know we have an array of strings...
        // If multiple values are allowed return this array value as-is.
        if (allowMultiple) {
            return v;
        }
        // At this point a single value is expected but we have an array
        // So we must reduce it to a single string.
        // We'll use the first item if there is one or an empty string if not.
        return v.length > 0 ? v[0] : '';
    };
    // Extracts the selected value or values from the provided options
    const getSelectedValues = (o) => {
        if (!o)
            return bufferValueType();
        return bufferValueType(o.filter(({ selected }) => selected).map(({ value }) => value) ||
            []);
    };
    // Handle change
    const handleChange = (v, o, suppressChangeEvent = false) => {
        const bufferedValue = bufferValueType(v);
        if (!suppressChangeEvent && onChange)
            onChange(bufferedValue, { options: o });
        setValue(bufferedValue);
        setOptions(o);
        return { options: o, value: bufferedValue };
    };
    // Sync provide option set to provide value(s)
    const syncOptionsToValue = (o, v) => {
        const newOptions = o.map((_a) => {
            var { value, selected } = _a, rest = __rest(_a, ["value", "selected"]);
            if (typeof v === 'string') {
                return Object.assign(Object.assign({}, rest), { selected: value === v, value });
            }
            return Object.assign(Object.assign({}, rest), { selected: v.includes(value) ? true : selected, value });
        });
        return newOptions;
    };
    //
    // Core state variables
    //
    const [value, setValue] = useState(bufferValueType(initialValue));
    const [options, setOptions] = useState(syncOptionsToValue(initialOptions, bufferValueType(initialValue)));
    //
    // Unified updaters
    //
    // Updates value and options based on a provided set of options
    const updateFromOptions = (o = [], suppressChangeEvent = false) => {
        if (o === options)
            return null;
        // Get an updated value based on the provided options
        const v = getSelectedValues(o);
        // Handle change
        return handleChange(v, o, suppressChangeEvent);
    };
    // Updates value and options based on a provided new selection
    const updateFromSelection = (selection, suppressChangeEvent = false) => {
        const { value: selectedValue, selected: nowSelected } = selection;
        // Check each option for a match with the selection value
        const o = options.map((_a) => {
            var { selected: wasSelected, value } = _a, rest = __rest(_a, ["selected", "value"]);
            let selected;
            // First make sure the selection value is valid
            if (!selectedValue || selectedValue.length === 0)
                selected = false;
            // Next if the selection value matches this option's value
            // set its `selected` to what is provided.
            // This allows us to not just set an option as selected
            // but also to toggle a selection in a multi-select context.
            else if (selectedValue === value)
                selected = nowSelected || false;
            // Last, if we don't have a match here we set either to false,
            // of if in a multi-select context, whatever the prior setting was.
            else
                selected = allowMultiple ? wasSelected || false : false;
            return Object.assign(Object.assign({}, rest), { selected,
                value });
        });
        // Get an updated value based on the new options
        const v = getSelectedValues(o);
        // Handle change
        return handleChange(v, o, suppressChangeEvent);
    };
    // Updates value and options based on a new value
    const updateFromValue = (v = bufferValueType(), suppressChangeEvent = false) => {
        if (v === value)
            return null;
        return handleChange(v, syncOptionsToValue(options, v), suppressChangeEvent);
    };
    return {
        options,
        updateFromOptions,
        updateFromSelection,
        updateFromValue,
        value,
    };
};
